import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, renderSlot as _renderSlot, unref as _unref, createVNode as _createVNode, resolveDynamicComponent as _resolveDynamicComponent, resolveComponent as _resolveComponent, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "flex" }
const _hoisted_2 = {
  key: 0,
  class: "text-sm text-gray-500 my-4"
}
const _hoisted_3 = { key: 0 }
const _hoisted_4 = {
  key: 1,
  class: "flex items-center space-x-2"
}
const _hoisted_5 = { class: "text-gray-700" }
const _hoisted_6 = {
  key: 0,
  class: "flex"
}
const _hoisted_7 = { key: 1 }
const _hoisted_8 = {
  key: 1,
  class: "mt-2 text-sm text-red-600"
}

import { computed, onMounted, ref, watch } from "vue"
import { EyeIcon } from "@heroicons/vue/outline"
import { useTagStore } from "@/stores/tag"
import { useOriginStore } from "@/stores/origin"
import { useLandingPageGroupStore } from "@/stores/landing-page-group"
import { useLifecycleStageStore } from "@/stores/lifecycle-stage"
import { useContactRoleStore } from "@/stores/contact-role"
import { useSalesStatusStore } from "@/stores/sales-status"
import GgmsChip from "@/components/GgmsChip.vue"

interface Props {
    modelValue?: any
    value?: any
    options: any[]
    label?: string
    description?: string
    optionLabel?: string
    optionValue?: string
    placeholder?: string
    isShowClear?: boolean
    class?: string
    panelClass?: string
    errorMessage?: string
    invalid?: boolean
    disabled?: boolean
    emptyMessage?: string
    small?: boolean
    inline?: boolean
    appendTo?: string
    required?: boolean
    isEditable?: boolean
    isShow?: boolean
    displayIcon?: boolean
    overwriteShowErrors?: boolean
    display?: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'GgmsDropdown',
  props: {
    modelValue: {},
    value: {},
    options: {},
    label: {},
    description: {},
    optionLabel: {},
    optionValue: {},
    placeholder: { default: "Select Element" },
    isShowClear: { type: Boolean, default: false },
    class: {},
    panelClass: {},
    errorMessage: {},
    invalid: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    emptyMessage: {},
    small: { type: Boolean },
    inline: { type: Boolean },
    appendTo: {},
    required: { type: Boolean, default: false },
    isEditable: { type: Boolean, default: false },
    isShow: { type: Boolean, default: false },
    displayIcon: { type: Boolean, default: false },
    overwriteShowErrors: { type: Boolean, default: false },
    display: { default: "" }
  },
  emits: ["update:modelValue", "onChange"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const tagStore = useTagStore()
const originStore = useOriginStore()
const landingPageGroupsStore = useLandingPageGroupStore()
const lifecycleStageStore = useLifecycleStageStore()
const contactRoleStore = useContactRoleStore()
const salesStatusStore = useSalesStatusStore()

const localModelValue = computed({
    get: () => props.modelValue || props.value,
    set: (value) => {
        emit("update:modelValue", value)
        emit("onChange", value)
    },
})

const localOptions = ref(props.options)
const showErrors = ref(true)
const localErrorMessage = ref(props.errorMessage)
const localInvalid = ref(props.invalid)
const isChipDisplay = computed(() => props.display === "chip")
const cachedTags = computed(() => tagStore.cachedTags)
const cachedOrigins = computed(() => originStore.cachedOrigins)
const cachedGroups = computed(() => landingPageGroupsStore.cachedGroups)
const cachedLifecycleStages = computed(() => lifecycleStageStore.cachedLifecycleStages)
const cachedContactRoles = computed(() => contactRoleStore.cachedRoles)
const cachedSalesStatuses = computed(() => salesStatusStore.cachedSalesStatuses)

const errorStyle = computed(() =>
    localInvalid.value && showErrors.value
        ? "border-red-300 placeholder-red-300 focus:ring-red-500 focus:border-red-500"
        : ""
)
const starStyle = computed(() => [localInvalid.value ? "text-red-600" : "text-gray-700", "pl-1"])

const inputStyle = computed(() => (props.small ? "h-[38px] text-sm" : ""))
const getWrapperStyleInline = computed(() => (props.inline ? "flex items-center gap-4" : ""))
const getLabelStyleInline = computed(() => (props.inline ? "w-2/5" : "mb-1"))
const getInputStyleInline = computed(() => (props.inline ? "w-80" : ""))

function disableValidationError() {
    localErrorMessage.value = ""
    localInvalid.value = false
    if (props.overwriteShowErrors) {
        showErrors.value = false
    }
}

function filterOptions(model: string | number) {
    const { optionValue, optionLabel } = props
    if (typeof model === "number" || typeof model === "object" || typeof model === "boolean") {
        return
    }

    const lowerCasedModel = model?.toLowerCase()

    if (lowerCasedModel && props.isEditable) {
        localOptions.value = props.options.filter((option) => {
            const label = optionLabel ? option[optionLabel] : option
            const value = optionValue ? option[optionValue] : option
            return label?.toLowerCase().includes(lowerCasedModel) || value?.toLowerCase().includes(lowerCasedModel)
        })
        return
    }
    localOptions.value = props.options
}

function removeChip() {
    localModelValue.value = undefined
}

function getIcon(value: any) {
    return localOptions.value.find((option) => option.value === value)?.icon
}

function getLabel(value: any) {
    return localOptions.value.find((option) => option.value === value)?.label
}

function getClass(value: any) {
    return localOptions.value.find((option) => option.value === value)?.class
}

onMounted(() => {
    disableValidationError()
})

watch(
    () => props.errorMessage,
    () => {
        localErrorMessage.value = props.errorMessage
        localInvalid.value = props.invalid
    }
)

watch(
    () => props.invalid,
    () => (localInvalid.value = props.invalid)
)

watch(
    () => props.options,
    () => {
        localOptions.value = props.options
    }
)

return (_ctx: any,_cache: any) => {
  const _component_Dropdown = _resolveComponent("Dropdown")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([props.class, getWrapperStyleInline.value])
  }, [
    _createElementVNode("div", _hoisted_1, [
      (_ctx.label)
        ? (_openBlock(), _createElementBlock("p", {
            key: 0,
            class: _normalizeClass([getLabelStyleInline.value, 'text-sm font-medium text-gray-700'])
          }, _toDisplayString(_ctx.label), 3))
        : _createCommentVNode("", true),
      (_ctx.required)
        ? (_openBlock(), _createElementBlock("span", {
            key: 1,
            class: _normalizeClass(starStyle.value)
          }, "*", 2))
        : _createCommentVNode("", true)
    ]),
    (_ctx.description)
      ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(_ctx.description), 1))
      : _createCommentVNode("", true),
    _createVNode(_component_Dropdown, {
      modelValue: localModelValue.value,
      "onUpdate:modelValue": [
        _cache[0] || (_cache[0] = ($event: any) => ((localModelValue).value = $event)),
        filterOptions
      ],
      options: localOptions.value,
      showClear: _ctx.isShowClear,
      optionLabel: _ctx.optionLabel,
      optionValue: _ctx.optionValue,
      placeholder: _ctx.placeholder,
      class: _normalizeClass([errorStyle.value, props.class, inputStyle.value, getInputStyleInline.value]),
      panelClass: _ctx.panelClass,
      disabled: _ctx.disabled,
      emptyMessage: _ctx.emptyMessage,
      appendTo: _ctx.appendTo,
      editable: _ctx.isEditable,
      onFocus: disableValidationError,
      onBlur: _cache[1] || (_cache[1] = ($event: any) => (showErrors.value = true))
    }, _createSlots({
      option: _withCtx((slotProps) => [
        (_ctx.displayIcon)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _renderSlot(_ctx.$slots, "customOption", { slotProps: slotProps }),
              (_openBlock(), _createBlock(_resolveDynamicComponent(slotProps.option.icon), {
                class: _normalizeClass([slotProps.option.class, "mr-2 w-5 h-5"])
              }, null, 8, ["class"])),
              _createElementVNode("span", null, _toDisplayString(slotProps.option.label), 1)
            ]))
          : (isChipDisplay.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createVNode(GgmsChip, {
                  bgColor: slotProps.option.backgroundColor,
                  textColor: slotProps.option.foregroundColor,
                  isRemovable: false
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(slotProps.option.displayName), 1)
                  ]),
                  _: 2
                }, 1032, ["bgColor", "textColor"])
              ]))
            : _createCommentVNode("", true)
      ]),
      _: 2
    }, [
      (isChipDisplay.value)
        ? {
            name: "value",
            fn: _withCtx((slotProps) => [
              (slotProps.value)
                ? (_openBlock(), _createBlock(GgmsChip, {
                    key: 0,
                    bgColor: 
                        cachedTags.value[slotProps.value]?.backgroundColor ??
                        cachedOrigins.value[slotProps.value]?.backgroundColor ??
                        cachedGroups.value[slotProps.value]?.backgroundColor ??
                        cachedLifecycleStages.value[slotProps.value]?.backgroundColor ??
                        cachedContactRoles.value[slotProps.value]?.backgroundColor ??
                        cachedSalesStatuses.value[slotProps.value]?.backgroundColor
                    ,
                    textColor: 
                        cachedTags.value[slotProps.value]?.foregroundColor ??
                        cachedOrigins.value[slotProps.value]?.foregroundColor ??
                        cachedGroups.value[slotProps.value]?.foregroundColor ??
                        cachedLifecycleStages.value[slotProps.value]?.foregroundColor ??
                        cachedContactRoles.value[slotProps.value]?.foregroundColor ??
                        cachedSalesStatuses.value[slotProps.value]?.foregroundColor
                    ,
                    class: "mr-2",
                    onOnRemove: removeChip
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(cachedTags.value[slotProps.value]?.displayName ??
                        cachedOrigins.value[slotProps.value]?.displayName ??
                        cachedGroups.value[slotProps.value]?.displayName ??
                        cachedLifecycleStages.value[slotProps.value]?.displayName ??
                        cachedContactRoles.value[slotProps.value]?.displayName ??
                        cachedSalesStatuses.value[slotProps.value]?.displayName), 1)
                    ]),
                    _: 2
                  }, 1032, ["bgColor", "textColor"]))
                : _createCommentVNode("", true)
            ]),
            key: "0"
          }
        : (!_ctx.optionValue)
          ? {
              name: "value",
              fn: _withCtx((slotProps) => [
                (!_ctx.isShow)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                      _renderSlot(_ctx.$slots, "customLabel", { slotProps: slotProps })
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      _createVNode(_unref(EyeIcon), {
                        class: "h-5 w-5 text-gray-500",
                        "aria-hidden": "true"
                      }),
                      _createElementVNode("span", _hoisted_5, "Show: " + _toDisplayString(localModelValue.value), 1)
                    ]))
              ]),
              key: "1"
            }
          : undefined
    ]), 1032, ["modelValue", "options", "showClear", "optionLabel", "optionValue", "placeholder", "class", "panelClass", "disabled", "emptyMessage", "appendTo", "editable"]),
    (localErrorMessage.value && showErrors.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(localErrorMessage.value), 1))
      : _createCommentVNode("", true)
  ], 2))
}
}

})